import { AxiosError } from 'axios';
import React, { createContext, useEffect, useState } from 'react';
import {
  DjangoListResponse,
  GenericObject,
  NotificationEntity,
} from 'v2/models';
import { handleError, ServerError } from 'v2/utils/handleError';
import restFactory from 'v2/utils/restFactory';

const defaultState = {
  unreadNotifications: { count: 0, results: [], prev: null, next: null },
};

export interface UseNotificationContext {
  unreadNotifications: DjangoListResponse<NotificationEntity>;
  refreshUnreadotifications?: () => Promise<void>;
}
const NotificationContext = createContext<UseNotificationContext>(defaultState);

function useNotificationContext(): UseNotificationContext {
  const context = React.useContext(NotificationContext);
  if (!context) {
    throw new Error(`useNotificationContext must be used within a FeatureFlag`);
  }
  return context;
}

function NotificationProvider(props: GenericObject) {
  const [unreadNotifications, setUnreadNotifications] = useState<
    UseNotificationContext['unreadNotifications']
  >(defaultState.unreadNotifications);

  useEffect(() => {
    const isDataProvider = props.user?.user?.data_provider;
    let interval: NodeJS.Timeout;

    if (isDataProvider) {
      interval = setInterval(() => {
        loadNotification();
      }, 60 * 1000);
      loadNotification();
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [props.user]);

  const loadNotification = async () => {
    try {
      const newUnreadNotifications = await restFactory.get<
        UseNotificationContext['unreadNotifications']
      >('/dj/api/v1/notification/?unread=true');
      setUnreadNotifications(newUnreadNotifications);
    } catch (error) {
      handleError(error as AxiosError<ServerError>);
    }
  };

  return (
    <NotificationContext.Provider
      {...props}
      value={{
        unreadNotifications: unreadNotifications,
        refreshUnreadotifications: loadNotification,
      }}
    />
  );
}

export { useNotificationContext, NotificationProvider };
